import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { Home, Plus, Edit, Archive, Eye, MapPin, Users, Bed } from 'lucide-react';

export default function HousesListPage() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [houses, setHouses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedHouse, setSelectedHouse] = useState(null);
  const [formData, setFormData] = useState({
    house_no: '',
    name: '',
    address: '',
    capacity: '',
    emergency_contact: '',
    emergency_phone: ''
  });

  useEffect(() => {
    fetchHouses();
  }, []);

  const fetchHouses = async () => {
    try {
      const response = await api.get('/houses');
      setHouses(response.data.items || []);
    } catch (error) {
      toast.error('Failed to load houses');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await api.post('/houses', {
        ...formData,
        capacity: parseInt(formData.capacity) || 0
      });
      toast.success('House created successfully');
      setShowCreateDialog(false);
      setFormData({ house_no: '', name: '', address: '', capacity: '', emergency_contact: '', emergency_phone: '' });
      fetchHouses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to create house');
    }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/houses/${selectedHouse.id}`, {
        ...formData,
        capacity: parseInt(formData.capacity) || 0
      });
      toast.success('House updated successfully');
      setShowEditDialog(false);
      setSelectedHouse(null);
      fetchHouses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update house');
    }
  };

  const handleArchive = async (houseId) => {
    if (!confirm('Are you sure you want to archive this house?')) return;
    try {
      await api.post(`/houses/${houseId}/archive`);
      toast.success('House archived successfully');
      fetchHouses();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to archive house');
    }
  };

  const openEditDialog = (house) => {
    setSelectedHouse(house);
    setFormData({
      house_no: house.house_no || '',
      name: house.name || '',
      address: house.address || '',
      capacity: house.capacity?.toString() || '',
      emergency_contact: house.emergency_contact || '',
      emergency_phone: house.emergency_phone || ''
    });
    setShowEditDialog(true);
  };

  const getStatusBadge = (status) => {
    const variants = {
      ACTIVE: 'default',
      INACTIVE: 'secondary',
      ARCHIVED: 'destructive'
    };
    return <Badge variant={variants[status] || 'secondary'}>{status}</Badge>;
  };

  if (loading) {
    return <div className="flex items-center justify-center min-h-screen"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;
  }

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-6" data-testid="houses-list-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Accommodation</h1>
          <p className="text-muted-foreground mt-1">
            {isAdmin ? 'Manage SIL houses and properties' : 'View SIL houses and properties'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={() => setShowCreateDialog(true)} data-testid="create-house-button">
            <Plus className="h-4 w-4 mr-2" />
            Add House
          </Button>
        )}
      </div>

      {/* Houses Grid */}
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
        {houses.map((house) => (
          <Card key={house.id} className="hover:shadow-lg transition-shadow" data-testid={`house-card-${house.id}`}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="space-y-1">
                  <CardTitle className="text-lg">{house.name}</CardTitle>
                  <CardDescription className="text-xs">{house.house_no}</CardDescription>
                </div>
                {getStatusBadge(house.status)}
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2 text-sm">
                <div className="flex items-start gap-2 text-muted-foreground">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="line-clamp-2">{house.address || 'No address'}</span>
                </div>
                <div className="flex items-center gap-4 text-muted-foreground">
                  <div className="flex items-center gap-1">
                    <Users className="h-4 w-4" />
                    <span>{house.capacity || 0} capacity</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Bed className="h-4 w-4" />
                    <span>{house.room_count || 0} rooms</span>
                  </div>
                </div>
              </div>

              <div className="flex gap-2 pt-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => navigate(`/houses/${house.id}`)}
                  data-testid={`view-house-${house.id}`}
                >
                  <Eye className="h-4 w-4 mr-1" />
                  View
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="flex-1"
                  onClick={() => navigate(`/accommodation/houses/${house.id}/rooms`)}
                >
                  <Bed className="h-4 w-4 mr-1" />
                  Rooms
                </Button>
                {isAdmin && (
                  <>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => openEditDialog(house)}
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    {house.status === 'ACTIVE' && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleArchive(house.id)}
                      >
                        <Archive className="h-4 w-4" />
                      </Button>
                    )}
                  </>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {houses.length === 0 && (
        <Card>
          <CardContent className="py-12">
            <div className="text-center space-y-2">
              <Home className="h-12 w-12 mx-auto text-muted-foreground" />
              <h3 className="font-medium">No houses yet</h3>
              <p className="text-sm text-muted-foreground">Create your first house to get started</p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent data-testid="create-house-dialog">
          <form onSubmit={handleCreate}>
            <DialogHeader>
              <DialogTitle>Create New House</DialogTitle>
              <DialogDescription>Add a new house/property to the system</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="house_no">House Number *</Label>
                <Input
                  id="house_no"
                  value={formData.house_no}
                  onChange={(e) => setFormData({ ...formData, house_no: e.target.value })}
                  placeholder="e.g., HOUSE-001"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Sunrise House"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Input
                  id="address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Full address"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="capacity">Capacity *</Label>
                <Input
                  id="capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  placeholder="Number of residents"
                  required
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_contact">Emergency Contact</Label>
                <Input
                  id="emergency_contact"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                  placeholder="Contact name"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="emergency_phone">Emergency Phone</Label>
                <Input
                  id="emergency_phone"
                  value={formData.emergency_phone}
                  onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                  placeholder="Phone number"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCreateDialog(false)}>Cancel</Button>
              <Button type="submit">Create House</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <form onSubmit={handleEdit}>
            <DialogHeader>
              <DialogTitle>Edit House</DialogTitle>
              <DialogDescription>Update house information</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label htmlFor="edit_house_no">House Number *</Label>
                <Input
                  id="edit_house_no"
                  value={formData.house_no}
                  onChange={(e) => setFormData({ ...formData, house_no: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_name">Name *</Label>
                <Input
                  id="edit_name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_address">Address *</Label>
                <Input
                  id="edit_address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_capacity">Capacity *</Label>
                <Input
                  id="edit_capacity"
                  type="number"
                  value={formData.capacity}
                  onChange={(e) => setFormData({ ...formData, capacity: e.target.value })}
                  required
                  min="1"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_emergency_contact">Emergency Contact</Label>
                <Input
                  id="edit_emergency_contact"
                  value={formData.emergency_contact}
                  onChange={(e) => setFormData({ ...formData, emergency_contact: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_emergency_phone">Emergency Phone</Label>
                <Input
                  id="edit_emergency_phone"
                  value={formData.emergency_phone}
                  onChange={(e) => setFormData({ ...formData, emergency_phone: e.target.value })}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowEditDialog(false)}>Cancel</Button>
              <Button type="submit">Save Changes</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
